import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import dayjs from 'dayjs'

import { localConfig } from '../../../../../../../../../Configs/localConfig'
import { errorAlert } from '../../../../../../../../../utils/alert'
import { useGetPropriedadesDoCliente } from '../../../../../../../../../services/atividades/GET'
import { useGetSafraConsultoria } from '../../../../../../../../../services/Safra/GET'
import { useGetEquipeCliente } from '../../../../../../../../../services/Equipe/GET'
import { useGetTalhoesPorPropriedade } from '../../../../../../../../../services/Talhao/GET'
import {
  useGetClienteAtual,
  useGetLaboratorios,
  useGetRepresentantesLegais,
  useGetTiposAnalise,
} from '../../../../../../../../../services/AgriculturaPrecisao/NewOSColeta/GET'
import { usePostOsParaColeta, usePutOsParaColeta } from '../../../../../../../../../services/OsParaColeta/POST'
import type { OsItensTypes } from '../../../../../../../../../services/OsParaColeta/GET'
import { dados_status } from '../../../meta'
import {
  formularioValido,
  validarFormularioOs,
  type ErrosFormulario,
} from '../meta/validarFormulario'
import { getCodTipoColeta, TipoColeta } from '../../../../../meta'

export type ResponsavelSelecionado = { descr: string; id: number; key?: number }

/** Modo de operação da gaveta. */
export type ModoForm = 'criar' | 'editar' | 'copiar'

/** Área de amostra no laudo (tabs 50/20 ha). */
export type AreaAmostra = '50 ha' | '20 ha'

/** Estado inicial dos campos — usado para criar e para resetar/detectar alterações. */
const CAMPOS_INICIAIS = {
  data_da_os: '' as string,
  outros_solicitantes: '' as string,
  propriedade: '' as string,
  propriedade_id: null as number | null,
  talhao: '' as string,
  talhao_id: null as number | null,
  laboratorio: '' as string,
  laboratorio_id: null as number | null,
  safra_consultoria: '' as string,
  safra_consultoria_id: null as number | null,
  tipo_de_analise: '' as string,
  tipo_de_analise_id: null as number | null,
  area_amostra_laudo: '50 ha' as AreaAmostra,
  responsaveis_selecionados: [] as ResponsavelSelecionado[],
  coleta_liberada: '' as string,
  coletada_em: '' as string,
  // Data de entrega das amostras ao laboratorio. Opcional; visivel a partir de COLETADA.
  data_entrega_lab: '' as string,
  representante_selecionado: '' as string,
  representante_selecionado_id: null as number | null,
  // usado apenas no modo editar
  status_atual: '' as string,
  status_atual_id: null as number | null,
}

type CamposForm = typeof CAMPOS_INICIAIS

/**
 * Formata uma data do estado para o payload. Retorna `null` quando vazia ou
 * inválida — o backend espera a ausência da data, não a string 'Invalid Date'
 * que o `dayjs('').format()` produziria.
 */
const formatarData = (valor: string): string | null => {
  if (!valor) return null
  const data = dayjs(valor)
  return data.isValid() ? data.format('YYYY-MM-DD') : null
}

interface UseFormularioOsParams {
  /** Fecha a gaveta (chamado após salvar com sucesso). */
  onClose: () => void
  /** cod da OS sendo editada/copiada (null quando criando). */
  cod_os: number | null
  /** Modo de operação. */
  modo: ModoForm
  /**
   * Tipo de coleta do submódulo dono da gaveta (ex: 'Solo').
   * Usado para pré-selecionar o "Tipo da análise" ao criar — o tipo é
   * determinado pelo submódulo, não é escolha do usuário.
   */
  tipo_coleta: TipoColeta
}

export function useFormularioOs({ onClose, cod_os, modo, tipo_coleta }: UseFormularioOsParams) {
  //#region CAMPOS DO FORMULÁRIO
  /** cod do tipo de coleta no backend (Solo=282; null se ainda não mapeado). */
  const cod_tipo_coleta = getCodTipoColeta(tipo_coleta)

  /** Estado inicial com o tipo de análise já pré-selecionado pelo submódulo. */
  const camposIniciais = useMemo<CamposForm>(
    () => ({
      ...CAMPOS_INICIAIS,
      tipo_de_analise: tipo_coleta,
      tipo_de_analise_id: cod_tipo_coleta,
    }),
    [tipo_coleta, cod_tipo_coleta]
  )

  const [campos, setCampos] = useState<CamposForm>(camposIniciais)

  /**
   * Snapshot do estado "limpo" para detectar alterações do usuário.
   * No criar é o estado inicial; no editar/copiar passa a ser o estado hidratado
   * com a OS carregada — sem isso a própria hidratação marcaria o form como sujo.
   */
  const [camposBase, setCamposBase] = useState<CamposForm>(camposIniciais)

  /**
   * Pendências da última tentativa de salvar, exibidas nas props `error`/
   * `errorMessage` de cada campo. Começa vazio: o formulário só acusa erro depois
   * que o usuário tenta salvar, não enquanto ainda está preenchendo.
   */
  const [erros, setErros] = useState<ErrosFormulario>({})

  /**
   * Qual erro cada campo do estado limpa ao ser preenchido. Os nomes diferem de
   * propósito: o estado guarda `propriedade_id`, o erro pertence ao campo visual
   * "propriedade".
   */
  const ERRO_DO_CAMPO: Partial<Record<keyof CamposForm, keyof ErrosFormulario>> = useMemo(
    () => ({
      data_da_os: 'data_da_os',
      propriedade_id: 'propriedade',
      talhao_id: 'talhao',
      laboratorio_id: 'laboratorio',
      safra_consultoria_id: 'safra_consultoria',
      responsaveis_selecionados: 'responsaveis',
      coleta_liberada: 'coleta_liberada',
      coletada_em: 'coletada_em',
      representante_selecionado_id: 'representante',
    }),
    []
  )

  /** Remove o erro de um campo assim que ele é preenchido. */
  const limparErroDoCampo = useCallback(
    (chave: keyof CamposForm) => {
      const chaveErro = ERRO_DO_CAMPO[chave]
      if (!chaveErro) return

      setErros((atual) => {
        if (!atual[chaveErro]) return atual
        const proximo = { ...atual }
        delete proximo[chaveErro]
        return proximo
      })
    },
    [ERRO_DO_CAMPO]
  )

  /**
   * Atualiza um campo específico. Aceita valor direto ou uma função updater
   * `(anterior) => novo` — necessário para componentes (ex: SelectMulti) que
   * chamam o onChange como um setState funcional.
   */
  const setCampo = useCallback(
    <K extends keyof CamposForm>(
      chave: K,
      valor: CamposForm[K] | ((anterior: CamposForm[K]) => CamposForm[K])
    ) => {
      setCampos((atual) => ({
        ...atual,
        [chave]: typeof valor === 'function' ? (valor as Function)(atual[chave]) : valor,
      }))
      limparErroDoCampo(chave)
    },
    [limparErroDoCampo]
  )

  /** Setter no formato Dispatch para os responsáveis (compatível com SelectMulti). */
  const setResponsaveis: Dispatch<SetStateAction<ResponsavelSelecionado[]>> = useCallback(
    (valor) => {
      setCampo('responsaveis_selecionados', valor as any)
    },
    [setCampo]
  )

  const resetForm = useCallback(() => {
    setCampos(camposIniciais)
    setCamposBase(camposIniciais)
    setErros({})
  }, [camposIniciais])

  /**
   * Pré-preenche TODOS os campos a partir de uma OS carregada, num único setState.
   * (No módulo antigo eram ~20 setStates + setTimeout(400) → causava a lentidão.)
   */
  const hidratar = useCallback((os: OsItensTypes) => {
    if (!os) return
    const statusInfo = dados_status.find((s) => s.cod === os.status)
    const hidratados: CamposForm = {
      data_da_os: os.data ? String(os.data) : '',
      outros_solicitantes: os.solicitante ?? '',
      propriedade: os.propriedade_nome ?? '',
      propriedade_id: os.cod_propriedade ?? null,
      talhao: os.talhao_nome ?? '',
      talhao_id: os.cod_talhao ?? null,
      laboratorio: os.laboratorio_nome ?? '',
      laboratorio_id: os.cod_laboratorio ?? null,
      // O backend não devolve o nome da safra, só o cod — o label é resolvido
      // pelo efeito abaixo, quando a lista de safras estiver carregada.
      safra_consultoria: os.safra_consultoria_nome ?? '',
      safra_consultoria_id: os.cod_safra_conta ?? null,
      // O tipo é fixo pelo submódulo; usa o cod da OS se vier, senão o do submódulo.
      tipo_de_analise: tipo_coleta,
      tipo_de_analise_id: os.cod_tipo ?? cod_tipo_coleta,
      area_amostra_laudo: os.cod_area_amostra === 20 ? '20 ha' : '50 ha',
      responsaveis_selecionados: (os.responsaveis ?? []).map((r) => ({
        descr: r?.responsavel_nome,
        id: r?.cod_responsavel,
        key: r?.cod_responsavel,
      })),
      coleta_liberada: os.data_liberada ? String(os.data_liberada) : '',
      coletada_em: os.data_coleta ? String(os.data_coleta) : '',
      data_entrega_lab: os.data_laboratorio ? String(os.data_laboratorio) : '',
      representante_selecionado: os.representante?.[0]?.solicitante ?? '',
      representante_selecionado_id: os.representante?.[0]?.cod ?? null,
      status_atual: statusInfo?.nome ?? '',
      status_atual_id: os.status ?? null,
    }
    setCampos(hidratados)
    // A OS carregada passa a ser a baseline: hidratar não é alteração do usuário.
    setCamposBase(hidratados)
    // Erros de uma tentativa anterior não valem para a OS recém-carregada.
    setErros({})
  }, [tipo_coleta, cod_tipo_coleta])

  //#region DADOS DE ORIGEM (react-query, com cache)
  const { data_propriedades, carregando_propriedades } = useGetPropriedadesDoCliente()
  const { data_safras: data_safra_consultoria } = useGetSafraConsultoria()
  const { data_equipe_cliente } = useGetEquipeCliente()
  const { data_laboratorios } = useGetLaboratorios()
  const { data_tipos_analise } = useGetTiposAnalise()
  const { data_representantes } = useGetRepresentantesLegais()
  const { data_cliente } = useGetClienteAtual()

  /**
   * Sincroniza o LABEL do tipo de análise com o nome real vindo do backend.
   * O id (cod_tipo do submódulo) é a fonte de verdade; o label começa com o
   * rótulo do tipo de coleta e é corrigido quando a lista de tipos carrega,
   * evitando divergência caso o `nome` no backend não seja idêntico ao rótulo.
   */
  useEffect(() => {
    if (!cod_tipo_coleta) return
    const tipo = (data_tipos_analise ?? []).find((t: any) => t?.cod === cod_tipo_coleta)
    if (tipo?.nome && tipo.nome !== campos.tipo_de_analise) {
      const corrigir = (atual: CamposForm) => ({
        ...atual,
        tipo_de_analise: tipo.nome,
        tipo_de_analise_id: cod_tipo_coleta,
      })
      setCampos(corrigir)
      // Correção automática de label não conta como alteração do usuário.
      setCamposBase(corrigir)
    }
  }, [data_tipos_analise, cod_tipo_coleta, campos.tipo_de_analise])

  /**
   * Resolve o LABEL da safra consultoria a partir do id.
   * O endpoint `/pedido-laboratorio` devolve apenas `cod_safra_conta` (o
   * `safra_consultoria_nome` vem vazio), então ao editar/copiar o Select
   * ficaria em branco. Buscamos o nome na lista de safras assim que ela
   * carregar — cobrindo também o caso da hidratação acontecer antes.
   */
  useEffect(() => {
    if (!campos.safra_consultoria_id) return
    const safra = (data_safra_consultoria ?? []).find((s) => s?.cod === campos.safra_consultoria_id)
    if (safra?.nome && safra.nome !== campos.safra_consultoria) {
      setCampos((atual) => ({ ...atual, safra_consultoria: safra.nome }))
      // Só resolve o label de uma safra que já estava na baseline (hidratação);
      // se o usuário acabou de escolher a safra, a baseline não tem esse id e
      // a alteração dele continua contando como "sujo".
      setCamposBase((base) =>
        base.safra_consultoria_id === campos.safra_consultoria_id
          ? { ...base, safra_consultoria: safra.nome }
          : base
      )
    }
  }, [data_safra_consultoria, campos.safra_consultoria_id, campos.safra_consultoria])

  /** Só propriedades ativas, memoizado para não quebrar memo dos selects. */
  const propriedades_ativas = useMemo(
    () => (data_propriedades ?? []).filter((p) => p?.status === 1),
    [data_propriedades]
  )

  /** Propriedade atualmente selecionada (objeto completo do cache). */
  const propriedade_atual = useMemo(
    () => propriedades_ativas.find((p) => p?.cod === campos.propriedade_id),
    [propriedades_ativas, campos.propriedade_id]
  )

  /** Matrícula/área derivadas da propriedade selecionada (sem estado extra). */
  const matricula = propriedade_atual?.matricula ?? ''
  const area_propriedade = propriedade_atual?.area ?? ''

  /** Talhões da propriedade selecionada — busca dedicada, igual ao módulo legado. */
  const { data_talhoes, carregando_talhoes } = useGetTalhoesPorPropriedade(campos.propriedade_id)
  const talhoes = useMemo(
    () => (data_talhoes ?? []).filter((t) => t?.cod_talhao === null),
    [data_talhoes]
  )

  /** Talhão selecionado (objeto completo). */
  const talhao_atual = useMemo(
    () => talhoes.find((t) => t?.cod === campos.talhao_id),
    [talhoes, campos.talhao_id]
  )

  /** Área (ha) do talhão selecionado — usada pela metade "Análises". */
  const area_talhao = useMemo(() => String(talhao_atual?.area ?? ''), [talhao_atual])

  /**
   * Contorno do talhão selecionado no formato [{lat, lng}] (usado pelo mapa
   * da metade "Análises": nova análise, editar contorno, criar malha).
   */
  const contorno_talhao = useMemo(
    () =>
      (talhao_atual?.kml?.coordenadas ?? []).map((coord) => ({
        lat: Number(coord?.latitude),
        lng: Number(coord?.longitude),
      })),
    [talhao_atual]
  )

  //#region AÇÕES COMPOSTAS
  /** Ao trocar a propriedade, limpa o talhão selecionado (pertencia à propriedade anterior). */
  const selecionarPropriedade = useCallback((nome: string) => {
    setCampos((atual) => ({ ...atual, propriedade: nome }))
  }, [])

  const selecionarPropriedadeId = useCallback((id: number | null) => {
    setCampos((atual) => ({ ...atual, propriedade_id: id, talhao: '', talhao_id: null }))
  }, [])

  //#region SALVAR
  const { carregando_salvar_os, mutate_os } = usePostOsParaColeta()
  const { carregando_editar_os, mutate_editar_os } = usePutOsParaColeta(cod_os ?? 0)

  const isEdicao = modo === 'editar'

  /**
   * Dados de análise necessários ao payload, fornecidos pela metade "Análises"
   * (hook useAnalises) no momento de salvar.
   */
  type DadosAnalisePayload = {
    analises: {
      title: string
      cod_analises: { cod: number; valor: number }
      id: string
      pontos: any[]
      valor_analise: number
    }[]
    malha_de_pontos: any
    valor: number
    /**
     * KML do contorno em base64, preenchido só quando o usuário editou o contorno
     * no mapa. `null` quando não houve edição — nesse caso a chave não é enviada
     * e o backend preserva o contorno já gravado.
     */
    arquivo_kml?: string | null
  }

  const montarPayload = useCallback(
    (refetchLista: () => void, dadosAnalise: DadosAnalisePayload) => {
      return {
        data: campos.data_da_os
          ? dayjs(campos.data_da_os).format('YYYY-MM-DD')
          : dayjs().format('YYYY-MM-DD'),
        // editar preserva o status escolhido; criar/copiar começa em ABERTA (1)
        status: isEdicao ? campos.status_atual_id ?? 1 : 1,
        cod_laboratorio: campos.laboratorio_id,
        // Só clientes do tipo 'G' (grupo) têm representante legal; nos demais o
        // campo nem é exibido e vai vazio, como na versão em produção.
        cod_representante: (campos.representante_selecionado_id ?? '') as number | '',
        // Vínculo da cópia com a OS de origem — o backend recebe um POST de OS
        // nova sabendo de qual OS ela foi clonada. Só existe no modo copiar.
        ...(modo === 'copiar' && cod_os ? { copiar: cod_os } : {}),
        cod_propriedade: campos.propriedade_id,
        cod_talhao: campos.talhao_id,
        cod_responsaveis: campos.responsaveis_selecionados?.map((r) => r?.id),
        cod_tipo: campos.tipo_de_analise_id,
        solicitante: campos.outros_solicitantes,
        cod_area_amostra: parseFloat(campos.area_amostra_laudo),
        cod_safra_conta: campos.safra_consultoria_id,
        // Datas opcionais: quando vazias vai `null` (igual ao módulo legado) — sem
        // isso o dayjs('') gera a string 'Invalid Date' no payload.
        data_liberada: formatarData(campos.coleta_liberada),
        data_coleta: formatarData(campos.coletada_em),
        // Opcional: o backend espera a chave `data_laboratorio`.
        data_laboratorio: formatarData(campos.data_entrega_lab),

        malha_de_pontos: dadosAnalise.malha_de_pontos,
        analises: dadosAnalise.analises,
        valor: dadosAnalise.valor,
        // Só vai quando o contorno foi editado no mapa — em criar, copiar ou editar.
        ...(dadosAnalise.arquivo_kml ? { arquivo_kml: dadosAnalise.arquivo_kml } : {}),

        refetch: () => {
          refetchLista()
          onClose()
        },
      }
    },
    [campos, onClose, isEdicao, modo, cod_os]
  )

  const salvar = useCallback(
    (refetchLista: () => void, dadosAnalise: DadosAnalisePayload) => {
      // O `obrigatorio` dos campos é só o asterisco visual — sem esta checagem a
      // OS seguiria incompleta e o erro só apareceria como recusa do backend.
      const pendencias = validarFormularioOs(campos, {
        isEdicao,
        ehGrupo: data_cliente?.tipo === 'G',
        possuiAnalises: (dadosAnalise?.analises?.length ?? 0) > 0,
      })

      setErros(pendencias)

      if (!formularioValido(pendencias)) {
        // Os campos exibem o próprio erro; o alerta cobre só o que não tem campo
        // na metade esquerda (análises) ou reforça quando o erro está fora de vista.
        if (pendencias.analises) {
          errorAlert(pendencias.analises)
        } else {
          errorAlert('Preencha os campos obrigatórios destacados.')
        }
        return
      }

      const payload = montarPayload(refetchLista, dadosAnalise)
      // editar -> PUT; criar e copiar -> POST (ver regra de negócio da migração)
      if (isEdicao) {
        mutate_editar_os(payload)
      } else {
        mutate_os(payload)
      }
    },
    [isEdicao, mutate_os, mutate_editar_os, montarPayload, data_cliente, campos]
  )

  //#region DERIVADOS
  /**
   * O usuário alterou algo? (usado para pedir confirmação ao fechar)
   * Compara o estado atual com a baseline — o estado inicial no criar ou a OS
   * hidratada no editar/copiar. Checar "tem algo preenchido" não serve: campos
   * pré-selecionados pelo submódulo (ex: tipo de análise) já nasceriam sujos.
   */
  const isDirty = useMemo(() => {
    const mudou = <K extends keyof CamposForm>(chave: K) => campos[chave] !== camposBase[chave]

    const responsaveisMudaram =
      campos.responsaveis_selecionados.length !== camposBase.responsaveis_selecionados.length ||
      campos.responsaveis_selecionados.some(
        (r, i) => r?.id !== camposBase.responsaveis_selecionados[i]?.id
      )

    return (
      mudou('data_da_os') ||
      mudou('outros_solicitantes') ||
      mudou('propriedade_id') ||
      mudou('talhao_id') ||
      mudou('laboratorio_id') ||
      mudou('safra_consultoria_id') ||
      mudou('tipo_de_analise_id') ||
      mudou('area_amostra_laudo') ||
      mudou('representante_selecionado_id') ||
      mudou('coleta_liberada') ||
      mudou('coletada_em') ||
      mudou('data_entrega_lab') ||
      mudou('status_atual_id') ||
      responsaveisMudaram
    )
  }, [campos, camposBase])

  return {
    // modo
    modo,
    isEdicao,
    cod_os,

    // campos + setter genérico
    campos,
    setCampo,
    setResponsaveis,
    resetForm,
    hidratar,

    // ações compostas
    selecionarPropriedade,
    selecionarPropriedadeId,

    // derivados de propriedade
    matricula,
    area_propriedade,
    propriedade_atual,
    talhoes,
    carregando_talhoes,
    area_talhao,
    contorno_talhao,

    // dados de origem
    propriedades_ativas,
    carregando_propriedades,
    data_safra_consultoria: data_safra_consultoria ?? [],
    data_equipe: data_equipe_cliente ?? [],
    data_laboratorios,
    data_tipos_analise,
    data_representantes,
    data_cliente,

    // validação (exibida nas props error/errorMessage dos campos)
    erros,

    // salvar
    salvar,
    carregando_salvar_os: carregando_salvar_os || carregando_editar_os,

    // fechar
    isDirty,
  }
}
